import React, { useState, useRef, useLayoutEffect, ChangeEvent, useEffect } from 'react';
import {
  Box,
  Button,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Divider,
  Stack,
  Pagination,
  TextField,
  InputAdornment,
  Tooltip,
  TableHead
} from '@mui/material';
import { getBuildingSites } from '../../components/shopping-cart/services/shopping-cart.service';
import { IBuildingSiteDto } from '../../components/order/interfaces/building-site.interface';
import { IBuildingSiteListViewDto } from '../../components/order/interfaces/building-site-list-view.interface';
import BuildingSitesListSkeleton from './building-sites-list-skeleton';
import { useTranslation } from '../../i18n/useTranslation';
import ButtonGroupList, { ButtonItem } from '../../shared/button-group-list/button-group-list';
import { htmlId } from './building-sites-list.service';
import styles from './building-sites-list.module.scss';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import BuildingSitesSelector from '../shopping-cart-container/building-sites/building-sites-selector';
import { getEditedBuildingSite, getLinkToBuildingSitesForm, getLinkToEditBuildingSitesForm, getLinkToViewBuildingSitesForm } from '../shopping-cart-container/building-sites/building-sites-form.service';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import globalStyles from '../../global-styles.module.scss';
import { BuildingSiteFormViewModel } from '../shopping-cart-container/building-sites/building-sites-form-view.model';
import { UserDetailsUseBetween } from '../../shared/use-between/user-details-use-between';
import { useBetween } from 'use-between';
import { Utils } from '../../shared/constants/utilities.constants';

const BuildingSiteList: React.FC = () => {
  const { t } = useTranslation('translation');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(0);
  const [bulidingSites, setBulidingSites] = useState<IBuildingSiteListViewDto[]>([]);
  const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(true);
  const [selectedBuildingSite, setSelectedBuildingSite] = useState<IBuildingSiteListViewDto>();
  const [buildingSiteSelectorIsOpen, setOpenBuildingSiteSelector] = useState(false);
  const { userSelection } = useBetween(UserDetailsUseBetween);
  const navigate = useNavigate();
  const searchActionButtons: ButtonItem[] = [
      {id: `${htmlId}-search-btn`, label: t('SEARCH')},
      {id: `${htmlId}-clear-search-btn`, label: t('CLEAR_SEARCH')}
  ];
  useEffect(() => {
      getAndSetBuildingSites(page);
  }, [page]);

  useEffect(() => {
    if (userSelection && userSelection.buildingSiteUuid && userSelection.buildingSiteUuid !== Utils.defaultUuid) {
      handlGetSlectedBulidingSite(userSelection.buildingSiteUuid);
    }
  }, [userSelection]);

  const handleSearchActionButtonClick = (id: string) => {
      switch (id) {
          case `${htmlId}-search-btn`:
            getAndSetBuildingSites(1);
          break;
          case `${htmlId}-clear-search-btn`:
            setSearch('');
            getAndSetBuildingSites(1, true);
          break;
      }
  }

  const getDto = (page: number, isClearSerach: boolean = false) => {
    return {
        data: [],
        count: count,
        page: page,
        searchInput: !isClearSerach ? search : '',
        pageSize: pageSize,
        fetchPerUserOnly: false
    } as IBuildingSiteDto;
  }

  const getAndSetBuildingSites = (page: number, isClearSerach: boolean = false) => {
    setIsSkeletonLoader(true);
    getBuildingSites(getDto(page, isClearSerach)).then((response) => {
        if (response) {
          const dto: IBuildingSiteDto = response.data;
          setBulidingSites(dto.data);
          setPage(dto.page);
          setCount(dto.count);
          setPageSize(dto.pageSize);
          setIsSkeletonLoader(false);
        }
    })
  }

   const handlGetSlectedBulidingSite = (contractorUuid: string) => {
      getEditedBuildingSite(contractorUuid).then((response) => {
          if (response) {
            const model: BuildingSiteFormViewModel = response.data;
            setSelectedBuildingSite({code: model.code, name: model.name, postalCode: model.postalCode, city: model.city, street: model.street, countryName: model.countryName} as IBuildingSiteListViewDto);
          }
      })
  }
  
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleCloseBuildigSiteDialog = (selectedBuildingSite: IBuildingSiteListViewDto | null) => {
      setOpenBuildingSiteSelector(false);
  }

  const getBuildingSiteValue = () => {
      if (selectedBuildingSite) {
          const streetName: string = selectedBuildingSite.street ?? '';
          const city: string = selectedBuildingSite.city ?? '';
          const postalCode: string = selectedBuildingSite.postalCode ?? '';
          const countryName: string = selectedBuildingSite.countryName ?? '';
          return ` ${selectedBuildingSite.name}   (${selectedBuildingSite.code})\n ${streetName}\n ${postalCode} ${city}\n ${countryName}`;
      }
      return '';
  }
  const selectedBuildingSiteExist = !!getBuildingSiteValue();

  return (
    <Box
      sx={{
        width: '100%',
        px: '150px',
        py: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
    <Box className={styles.form_content}>
      <Box className={styles.two_columns}>
        <Box className={styles.column}>
          <Typography className={styles.input_label}>{t('SELECTED_BUILDING_SITE')}*</Typography>
          <TextField
              id={`${htmlId}-building-site-input`}
              className={styles.text_field}
              onClick={() => {setOpenBuildingSiteSelector(true)}}
              value={getBuildingSiteValue()}
              variant="outlined"
              multiline
              label={!selectedBuildingSiteExist ? t('SELECT_BUILDING_SITE') : ''}
              InputProps={{
                  style: {cursor: 'pointer'},
                  readOnly: true,
                  endAdornment: (
                  <InputAdornment 
                      position="end"
                      sx={{cursor: 'pointer'}}
                      onClick={() => {setOpenBuildingSiteSelector(true)}}
                  >
                      {!selectedBuildingSiteExist ? <ArrowDropDownIcon/> : <EditIcon sx={{fontSize: '20px'}}/>}
                  </InputAdornment>
                  ),
              }}
              name="buildingSite"
              size='small'
              sx={{
                  '& .MuiInputBase-input': { whiteSpace: 'pre-wrap', cursor: 'pointer' },
                  '& .MuiOutlinedInput-notchedOutline': {
                      border: selectedBuildingSiteExist ? 'none' : undefined,
                  },
                  backgroundColor: selectedBuildingSiteExist ? '#fafafa' : 'transparent',
              }}
              fullWidth                
          />
            <React.Fragment>
                <BuildingSitesSelector
                    isOpen={buildingSiteSelectorIsOpen} 
                    handleCloseDialog={(value: IBuildingSiteListViewDto | null) => {
                        handleCloseBuildigSiteDialog(value);
                    }}/>
            </React.Fragment>
          </Box>
           <Box className={styles.column}>
            <section className={styles.add_building_site_section}>
              <Button
                onClick={() => {
                  navigate(getLinkToBuildingSitesForm());
                }}
                sx={{
                width: 'max-content',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textTransform: 'none',
                flexShrink: 0,
                backgroundColor: '#821818',
                color: 'white',
                fontWeight: 600,
                fontSize: '14px',
                '&: hover': {
                    backgroundColor: '#821818',
                    boxShadow: 'none'
                },
                borderRadius: 'unset',
                }}>
                {t('ADD_BUILDING_SITE')}
              </Button>
            </section>
           </Box>
        </Box>
      </Box>
      <Paper
        sx={{
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          border: 'none',
          gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, marginBottom: 0, minWidth: '100%' }}>
            <TextField
                label= {t('SEARCH')}
                variant="outlined"
                value={search}
                onChange={handleSearchChange}
                size='small'
                InputProps={{
                    endAdornment: (
                    <InputAdornment position="end">
                        <ButtonGroupList buttons={searchActionButtons} size='small' onClick={(id: string) => {handleSearchActionButtonClick(id)}} />
                    </InputAdornment>
                    ),
                }}
            fullWidth
            />
          </Box>
        </Paper>
        {isSkeletonLoader ? <BuildingSitesListSkeleton/> : 
            <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              maxHeight: '50vh',
              transition: 'max-height 0.3s ease',
            }}
            >
             <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table>
                  <TableHead>
                      <TableRow>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('CODE')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('BUILDING_SITE')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('NAME')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>{t('ADDRESS')}</strong>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}></TableCell>
                      </TableRow>
                  </TableHead>

                  <TableBody>
                      {bulidingSites
                      .map((item, index) => (
                          <TableRow
                              key={item.code}
                              sx={{
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                              }}
                          >
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.code}
                              {item.isShared ? <ShareIcon style={{'fontSize': "17px", 'marginLeft': "5px"}}></ShareIcon> : ''}
                              {item.isShared ?<Tooltip placement={'right'} title={t('BUILDING_SITES_ITEMS.MESSAGE_1')}><span className={styles.shared_icon_text}>?</span></Tooltip> : ''}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.secondName}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.name}
                          </TableCell>
                          <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              <Box>
                              <Typography variant="body2">{item.street}</Typography>
                              <Typography variant="body2">{item.postalCode} {item.city}</Typography>
                              <Typography variant="body2">{item.countryName}</Typography>
                              </Box>
                          </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                paddingY: 1,
                                verticalAlign: 'middle',
                                width: '1%',
                                whiteSpace: 'nowrap',
                                borderBottom: '1px solid #e0e0e0',
                              }}
                            >
                            {!item.isShared ? 
                              <Button
                                onClick={() => {
                                  navigate(getLinkToEditBuildingSitesForm(item.id))
                                }}
                                sx={{
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textTransform: 'none',
                                flexShrink: 0,
                                backgroundColor: '#821818',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '14px',
                                '&: hover': {
                                    backgroundColor: '#821818',
                                    boxShadow: 'none'
                                },
                                borderRadius: 'unset',
                              }}>
                                {t('EDIT')}
                              </Button> : 
                              <Button
                                onClick={() => {
                                  navigate(getLinkToViewBuildingSitesForm(item.id))
                                }}
                                sx={{
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textTransform: 'none',
                                flexShrink: 0,
                                backgroundColor: '#821818',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '14px',
                                '&: hover': {
                                    backgroundColor: '#821818',
                                    boxShadow: 'none'
                                },
                                borderRadius: 'unset',
                              }}>
                              {t('VIEW')}
                              </Button>
                            }
                            </TableCell>
                          </TableRow>
                      ))}
                  </TableBody>
                </Table>
            </TableContainer>
          </Box>
      }
      <Stack direction="row" justifyContent="center">
        <Pagination
          count={count}
          page={page}
          onChange={handleChangePage}
          sx={{
            '& .MuiPaginationItem-root': {
              color: 'black',
              borderColor: '#821818 !important',
            },
            '& .Mui-selected': {
              backgroundColor: '#821818 !important',
              color: 'white',
              '&:hover': {
                backgroundColor: '#821818 !important',
              },
            },
          }}
        />
      </Stack>
    </Box>
  );
};
export default BuildingSiteList;